import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Plus, Building, Edit, Users, Euro } from "lucide-react";

function Habitaciones() {
	const { user } = useAuth();
	const [habitaciones, setHabitaciones] = useState([]);
	const [loading, setLoading] = useState(true);
	const [showModal, setShowModal] = useState(false);
	const [editingHabitacion, setEditingHabitacion] = useState(null);

	const [formData, setFormData] = useState({
		numero: "",
		precio_mensual: "",
		descripcion: "",
	});

	useEffect(() => {
		cargarHabitaciones();
	}, []);

	const cargarHabitaciones = async () => {
		try {
			setLoading(true);
			const response = await axios.get("/api/habitaciones");
			setHabitaciones(response.data);
		} catch (error) {
			console.error("Error al cargar habitaciones:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		try {
			if (editingHabitacion) {
				await axios.put(`/api/habitaciones/${editingHabitacion.id}`, formData);
			} else {
				await axios.post("/api/habitaciones", formData);
			}

			setShowModal(false);
			setEditingHabitacion(null);
			setFormData({ numero: "", precio_mensual: "", descripcion: "" });
			cargarHabitaciones();
		} catch (error) {
			console.error("Error al guardar habitación:", error);
			alert(error.response?.data?.message || "Error al guardar la habitación");
		}
	};

	const openEditModal = (habitacion) => {
		setEditingHabitacion(habitacion);
		setFormData({
			numero: habitacion.numero,
			precio_mensual: habitacion.precio_mensual,
			descripcion: habitacion.descripcion || "",
		});
		setShowModal(true);
	};

	const openCreateModal = () => {
		setEditingHabitacion(null);
		setFormData({ numero: "", precio_mensual: "", descripcion: "" });
		setShowModal(true);
	};

	const formatearMoneda = (cantidad) => {
		return new Intl.NumberFormat("es-ES", {
			style: "currency",
			currency: "EUR",
		}).format(cantidad);
	};

	if (loading) {
		return (
			<div className="loading">
				<div className="spinner"></div>
			</div>
		);
	}

	return (
		<div className="container">
			<div className="page-header">
				<div>
					<h1>Gestión de Habitaciones</h1>
					<p>Administra las habitaciones del piso</p>
				</div>
				<div className="header-actions">
					{user?.tipo_usuario === "gestor" && (
						<button onClick={openCreateModal} className="btn btn-primary">
							<Plus size={16} />
							Nueva Habitación
						</button>
					)}
				</div>
			</div>

			<div className="habitaciones-grid">
				{habitaciones.length === 0 ? (
					<div className="empty-state">
						<Building
							size={64}
							style={{ color: "#d1d5db", marginBottom: "16px" }}
						/>
						<h3>No hay habitaciones configuradas</h3>
						<p>Agrega la primera habitación para comenzar</p>
					</div>
				) : (
					habitaciones.map((habitacion) => (
						<div key={habitacion.id} className="habitacion-card">
							<div className="habitacion-header">
								<div className="habitacion-icon">
									<Building size={24} />
								</div>
								<div className="habitacion-info">
									<h3>{habitacion.numero}</h3>
									<p className="precio">
										{formatearMoneda(habitacion.precio_mensual)}
										/mes
									</p>
								</div>
								{user?.tipo_usuario === "gestor" && (
									<button
										onClick={() => openEditModal(habitacion)}
										className="btn-icon btn-outline"
									>
										<Edit size={16} />
									</button>
								)}
							</div>

							{habitacion.descripcion && (
								<div className="habitacion-description">
									<p>{habitacion.descripcion}</p>
								</div>
							)}

							<div className="habitacion-stats">
								<div className="stat">
									<Users size={16} />
									<span>
										{habitacion.usuarios_count || 0} inquilino
										{(habitacion.usuarios_count || 0) !== 1 ? "s" : ""}
									</span>
								</div>
								{habitacion.usuarios_nombres && (
									<div className="inquilinos">
										<small>{habitacion.usuarios_nombres}</small>
									</div>
								)}
							</div>
						</div>
					))
				)}
			</div>

			{/* Modal crear/editar habitación */}
			{showModal && (
				<div className="modal-overlay" onClick={() => setShowModal(false)}>
					<div className="modal" onClick={(e) => e.stopPropagation()}>
						<h2>
							{editingHabitacion ? "Editar Habitación" : "Nueva Habitación"}
						</h2>

						<form onSubmit={handleSubmit}>
							<div className="form-group">
								<label className="form-label">Número de Habitación *</label>
								<input
									type="text"
									value={formData.numero}
									onChange={(e) =>
										setFormData({
											...formData,
											numero: e.target.value,
										})
									}
									className="form-input"
									placeholder="Ej: Habitación 1"
									required
								/>
							</div>

							<div className="form-group">
								<label className="form-label">Precio Mensual *</label>
								<div className="input-with-icon">
									<Euro size={16} className="input-icon" />
									<input
										type="number"
										step="0.01"
										value={formData.precio_mensual}
										onChange={(e) =>
											setFormData({
												...formData,
												precio_mensual: e.target.value,
											})
										}
										className="form-input"
										placeholder="400.00"
										required
									/>
								</div>
							</div>

							<div className="form-group">
								<label className="form-label">Descripción</label>
								<textarea
									value={formData.descripcion}
									onChange={(e) =>
										setFormData({
											...formData,
											descripcion: e.target.value,
										})
									}
									className="form-textarea"
									rows="3"
									placeholder="Descripción de la habitación..."
								/>
							</div>

							<div className="modal-actions">
								<button
									type="button"
									onClick={() => setShowModal(false)}
									className="btn btn-outline"
								>
									Cancelar
								</button>
								<button type="submit" className="btn btn-primary">
									{editingHabitacion ? "Actualizar" : "Crear"} Habitación
								</button>
							</div>
						</form>
					</div>
				</div>
			)}

			<style jsx>{`
				.page-header {
					display: flex;
					justify-content: space-between;
					align-items: flex-start;
					margin-bottom: 32px;
				}

				.page-header h1 {
					font-size: 32px;
					font-weight: 700;
					color: #1f2937;
					margin-bottom: 8px;
				}

				.page-header p {
					color: #6b7280;
				}

				.habitaciones-grid {
					display: grid;
					grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
					gap: 24px;
				}

				.empty-state {
					grid-column: 1 / -1;
					text-align: center;
					padding: 64px 24px;
					background: white;
					border-radius: 12px;
					border: 2px dashed #e5e7eb;
				}

				.empty-state h3 {
					font-size: 20px;
					color: #374151;
					margin-bottom: 8px;
				}

				.empty-state p {
					color: #6b7280;
				}

				.habitacion-card {
					background: white;
					border-radius: 12px;
					padding: 24px;
					box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
					transition: transform 0.2s, box-shadow 0.2s;
				}

				.habitacion-card:hover {
					transform: translateY(-2px);
					box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
				}

				.habitacion-header {
					display: flex;
					align-items: flex-start;
					gap: 16px;
					margin-bottom: 16px;
				}

				.habitacion-icon {
					width: 48px;
					height: 48px;
					background: linear-gradient(135deg, #3b82f6, #1d4ed8);
					border-radius: 12px;
					display: flex;
					align-items: center;
					justify-content: center;
					color: white;
					flex-shrink: 0;
				}

				.habitacion-info {
					flex: 1;
				}

				.habitacion-info h3 {
					font-size: 18px;
					font-weight: 600;
					color: #1f2937;
					margin-bottom: 4px;
				}

				.precio {
					font-size: 16px;
					font-weight: 600;
					color: #10b981;
					margin: 0;
				}

				.btn-icon {
					width: 36px;
					height: 36px;
					display: flex;
					align-items: center;
					justify-content: center;
					border-radius: 8px;
					border: 1px solid #d1d5db;
					background: white;
					color: #6b7280;
					cursor: pointer;
					transition: all 0.2s;
				}

				.btn-icon:hover {
					background: #f9fafb;
					color: #374151;
				}

				.habitacion-description {
					margin-bottom: 16px;
					padding: 12px;
					background: #f9fafb;
					border-radius: 8px;
				}

				.habitacion-description p {
					color: #6b7280;
					font-size: 14px;
					margin: 0;
				}

				.habitacion-stats {
					border-top: 1px solid #e5e7eb;
					padding-top: 16px;
				}

				.stat {
					display: flex;
					align-items: center;
					gap: 8px;
					color: #6b7280;
					font-size: 14px;
					margin-bottom: 8px;
				}

				.inquilinos {
					margin-top: 8px;
				}

				.inquilinos small {
					color: #9ca3af;
					font-size: 12px;
				}

				.input-with-icon {
					position: relative;
				}

				.input-icon {
					position: absolute;
					left: 12px;
					top: 50%;
					transform: translateY(-50%);
					color: #6b7280;
				}

				.input-with-icon .form-input {
					padding-left: 40px;
				}

				.modal-actions {
					display: flex;
					gap: 12px;
					justify-content: flex-end;
					margin-top: 24px;
				}

				@media (max-width: 768px) {
					.page-header {
						flex-direction: column;
						gap: 16px;
					}

					.habitaciones-grid {
						grid-template-columns: 1fr;
					}

					.habitacion-header {
						flex-direction: column;
						align-items: center;
						text-align: center;
					}

					.btn-icon {
						align-self: flex-end;
					}
				}
			`}</style>
		</div>
	);
}

export default Habitaciones;
